import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  MerchantService,
  NotificationService,
  TestMidCondition,
  VTService,
} from '@odin/odin-core';
import {
  PEBLFormData,
  PEBLPaymentRequest,
  PEBLPaymentResponse,
  PeblSendType,
  ScheduleResponse,
  isScheduleResponse,
} from '@odin/odin-transactions';
import { CountryConvertService } from '@odin/odin-core';

@Component({
  standalone: false,
  selector: 'odin-pebl-confirmation-dialog',
  templateUrl: './pebl-confirmation-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./pebl-confirmation-dialog.component.scss'],
})
export class PeblConfirmationDialogComponent {
  public processing: boolean = false;
  constructor(
    public dialogRef: MatDialogRef<PeblConfirmationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public peblFormData: PEBLFormData,
    private vtService: VTService,
    private notificationService: NotificationService,
    private merchantService: MerchantService,
    private countryConvertService: CountryConvertService
  ) { }

  public get currencyAlphaCode(): string {
    const countryData = this.countryConvertService.GetCountryByCurrencyNumericCode(this.peblFormData.currencyCode);
    return countryData ? countryData.currencyCode : 'GBP';
  }

  public SendTypeMapper(sendtype: PeblSendType): string {
    switch (sendtype) {
      case PeblSendType.EMAIL:
        return `Email - ${this.peblFormData.emailAddress}`;
      case PeblSendType.SMS:
        return `SMS - ${this.peblFormData.phoneNumber}`;
      case PeblSendType.LINK:
        return 'Direct Link';
    }
    return 'Unknown - Error';
  }

  public deliveryIcon(): string {
    switch (this.peblFormData.method) {
      case PeblSendType.EMAIL:
        return 'email';
      case PeblSendType.SMS:
        return 'phone_iphone';
      case PeblSendType.LINK:
        return 'assignment_turned_in';
    }
    return 'link';
  }

  public LinkExpiryMapper(time: string): string {
    switch (time) {
      case '60':
        return '1 Hour';
      case '1440':
        return '24 Hours';
      case '10080':
        return '1 Week';
      case '43800':
        return '1 Month';
    }
    return 'Unknown - Error';
  }

  public MakePEBLRequest(): void {
    this.processing = true;
    const reqBody = new PEBLPaymentRequest().build(this.peblFormData);

    if (this.merchantService.getTestMidCondition() === TestMidCondition.Limited) {
      this.notificationService.AlertDialogWindow(
        "The data entered is valid, but "
        + "the currently selected MID cannot create a payment link."
      );
      this.processing = false;
      return;
    }

    this.vtService.CreatePeblRequest(reqBody).subscribe(
      (response: PEBLPaymentResponse | ScheduleResponse) => {
        this.processing = false;
        if (isScheduleResponse(response)) {
          this.notificationService.SmallDialog(
            'Payment link scheduled',
            'Close',
            10000,
          );
          this.dialogRef.close({ status: true, scheduleResp: response, errors: [] });
        } else {
          this.notificationService.SmallDialog(response.message, 'Close', 10000);
          this.dialogRef.close({ status: true, resp: response, errors: [] });
        }
      },
      (err: HttpErrorResponse) => {
        this.processing = false;
        // handle errors
        const error = (err.error ?? {}) as {
          message: string;
          errors: { errorMessage: string; propertyName: string }[];
        };
        if (error.errors === undefined || error.errors.length === 0) {
          this.notificationService.ErrorSmallDialog(
            'Something went wrong.',
            err);
          return;
        }
        if (error.errors.length > 1) {
          // handle multiple errors
          this.notificationService.ErrorSmallDialog(
            `Multiple errors detected.
            ${error.errors.map((e) => e.errorMessage).join('\n')}`,
            err);
        } else {
          this.notificationService.ErrorSmallDialog(
            `${error.errors[0].errorMessage}`,
            err);
        }
        this.dialogRef.close({ status: false, errors: error.errors });
      },
    );
  }
}

